import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import BackButton from '../../common/BackButton';
import { YogaRecord, YogaMenu } from '../../../constants/Records';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(8),
  },
  userInfo: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  paper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  formControl: {
    minWidth: 200,
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

interface YogaRecordFormProps {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  menus: YogaMenu[];
  currentRecord: YogaRecord | null;
  isEdit: boolean;
  executing: boolean;
  created: boolean;
  updated: boolean;
  onFetchRecord: (uniqueId: string, recordTime: string) => void;
  onFetchMenus: (uniqueId: string) => void;
  onCreate: (data: Partial<YogaRecord>) => void;
  onUpdate: (uniqueId: string, recordTime: string, data: Partial<YogaRecord>) => void;
  onResetFlags: () => void;
}

const YogaRecordForm: React.FC<YogaRecordFormProps> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  menus,
  currentRecord,
  isEdit,
  executing,
  created,
  updated,
  onFetchRecord,
  onFetchMenus,
  onCreate,
  onUpdate,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [menuId, setMenuId] = useState<number | ''>('');
  const [recordTime, setRecordTime] = useState<string>('');

  // コンテナ側で初期化時にメニューを取得するため、ここでは呼び出し不要

  // 編集モードの場合、既存レコードの値をセット
  useEffect(() => {
    if (isEdit && currentRecord) {
      setMenuId(currentRecord.menu_id);
      setRecordTime(currentRecord.record_time);
    }
  }, [isEdit, currentRecord]);

  // 作成/更新成功時に一覧に戻る
  useEffect(() => {
    if (created || updated) {
      onResetFlags();
      history.push(`/institutions/${institutionId}/users/${userId}/records/yoga`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, updated]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (menuId === '') {
      return;
    }

    const data: Partial<YogaRecord> = {
      unique_id: uniqueId,
      menu_id: menuId as number,
    };

    if (isEdit && currentRecord) {
      onUpdate(uniqueId, currentRecord.record_time, data);
    } else {
      onCreate(data);
    }
  };

  const handleCancel = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/yoga`);
  };

  // メニューをカテゴリごとにグループ化
  const groupedMenus = menus.reduce(
    (acc, menu) => {
      const category = menu.category_name || 'その他';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(menu);
      return acc;
    },
    {} as Record<string, YogaMenu[]>,
  );

  return (
    <div className={classes.root}>
      <BackButton />

      <Box className={classes.userInfo}>
        <Typography variant="subtitle2" color="textSecondary">
          施設名: {institutionName}
        </Typography>
        <Typography variant="h6">利用者: {userName} 様</Typography>
      </Box>

      <Typography variant="h5">{isEdit ? 'ADLヨガ記録 - 編集' : 'ADLヨガ記録 - 新規作成'}</Typography>

      <Paper className={classes.paper}>
        {executing ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit} className={classes.form}>
            {isEdit && (
              <TextField
                label="記録日時"
                value={recordTime}
                disabled
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            )}

            <FormControl variant="outlined" className={classes.formControl} fullWidth required>
              <InputLabel id="menu-select-label">メニュー</InputLabel>
              <Select
                labelId="menu-select-label"
                value={menuId}
                onChange={e => setMenuId(e.target.value as number)}
                label="メニュー"
              >
                {Object.entries(groupedMenus).map(([category, categoryMenus]) => [
                  <MenuItem key={`category-${category}`} disabled style={{ fontWeight: 'bold' }}>
                    {category}
                  </MenuItem>,
                  ...categoryMenus.map(menu => (
                    <MenuItem key={menu.id} value={menu.id} style={{ paddingLeft: 24 }}>
                      {menu.menu_name}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>

            <Box className={classes.buttonContainer}>
              <Button variant="outlined" onClick={handleCancel} disabled={executing}>
                キャンセル
              </Button>
              <Button type="submit" variant="contained" color="primary" disabled={executing || menuId === ''}>
                {executing ? '処理中...' : '保存'}
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </div>
  );
};

export default YogaRecordForm;
